// Shared configuration for the free founder tools. Edit figures here — every tool and the homepage teaser read from this file.

export const TOOL_PATHS = {
  profit: "/tools/profit-to-pocket",
  setup: "/tools/company-setup-estimator",
  calendar: "/tools/compliance-calendar",
  roadmap: "/tools/relocation-roadmap",
};

// ---------- Profit to Pocket (Cyprus rules in force from 1 January 2026) ----------
export const TAX_CONFIG = {
  year: 2026,
  corporateTax: 0.15,
  sdcDividend: { non_dom: 0, domiciled: 0.05 },
  gesyDividend: 0.0265,
  gesyIncomeCap: 180000,
  defaultProfit: 250000,
};

export function calcProfitToPocket({ profit, status = "non_dom", distribution = 100 }) {
  const p = Math.max(0, Number(profit) || 0);
  const share = Math.min(100, Math.max(0, Number(distribution))) / 100;
  const cit = p * TAX_CONFIG.corporateTax;
  const afterCit = p - cit;
  const dividend = afterCit * share;
  const retained = afterCit - dividend;
  const sdcRate = TAX_CONFIG.sdcDividend[status] ?? 0;
  const sdc = dividend * sdcRate;
  const gesy = Math.min(dividend, TAX_CONFIG.gesyIncomeCap) * TAX_CONFIG.gesyDividend;
  const pocket = dividend - sdc - gesy;
  const totalTax = cit + sdc + gesy;
  return { profit: p, cit, afterCit, dividend, retained, sdcRate, sdc, gesy, pocket, totalTax, effectiveRate: p ? totalTax / p : 0 };
}

// ---------- Company Setup Estimator (indicative EUR ranges: [min, max]) ----------
export const SETUP_CONFIG = {
  base: { setup: [1800, 2600], annual: [1600, 2400], weeks: [1, 2] },
  types: {
    holding: { setup: [0, 0], annual: [0, 500], weeks: [0, 0] },
    trading: { setup: [400, 900], annual: [1200, 2500], weeks: [0, 1] },
    regulated: { setup: [15000, 40000], annual: [12000, 30000], weeks: [16, 40] },
  },
  // quote: true => priced on quotation, excluded from totals
  extras: {
    bank: { setup: [1500, 2500], annual: [0, 0], weeks: [2, 6] },
    vat: { setup: [140, 140], annual: [250, 250], weeks: [1, 3] },
    director: { quote: true, weeks: [0, 0] },
    office: { quote: true, weeks: [1, 3] },
    payroll: { quote: true, weeks: [0, 1] },
  },
  extraShareholdersQuote: true,
  maxShareholders: 10,
};

export function calcSetup({ type, shareholders, extras }) {
  const parts = [SETUP_CONFIG.base, SETUP_CONFIG.types[type], ...extras.map((e) => SETUP_CONFIG.extras[e])];
  const priced = parts.filter((p) => !p.quote);
  const sum = (key, i) => priced.reduce((acc, p) => acc + p[key][i], 0);
  const parallelWeeks = (i) => SETUP_CONFIG.base.weeks[i] + Math.max(...parts.slice(1).map((p) => p.weeks[i]), 0);
  const quoted = extras.filter((e) => SETUP_CONFIG.extras[e].quote);
  if (shareholders > 1 && SETUP_CONFIG.extraShareholdersQuote) quoted.push("shareholders");
  return {
    setup: [sum("setup", 0), sum("setup", 1)],
    annual: [sum("annual", 0), sum("annual", 1)],
    weeks: [parallelWeeks(0), parallelWeeks(1)],
    quoted,
  };
}

// ---------- Compliance Calendar ----------
// kind: annual {month, day} | fye {months} (last day of the month N months after year-end) | quarterly {monthsAfter, day} | monthly {day} | monthEnd
export const COMPLIANCE_RULES = [
  { id: "tax_return", kind: "fye", months: 15 },
  { id: "accounts", kind: "fye", months: 12 },
  { id: "ubo_validation", kind: "annual", month: 12, day: 31 },
  { id: "provisional_1", kind: "annual", month: 7, day: 31 },
  { id: "provisional_2", kind: "annual", month: 12, day: 31 },
  { id: "final_tax", kind: "annual", month: 8, day: 1 },
  { id: "vat", kind: "quarterly", monthsAfter: 2, day: 10, when: "vat" },
  { id: "vies", kind: "monthly", day: 15, when: "intraEu" },
  { id: "payroll", kind: "monthEnd", when: "employees" },
  { id: "employer_return", kind: "annual", month: 7, day: 31, when: "employees" },
];
export const STANDING_OBLIGATIONS = ["ubo_initial", "ubo", "registers", "substance"];

const lastDay = (y, m0) => new Date(y, m0 + 1, 0);

export function buildCalendar({ fyeMonth = 12, flags = {}, from = new Date(), months = 12 }) {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(start.getFullYear(), start.getMonth() + months, start.getDate());
  const out = [];
  const push = (id, d) => d >= start && d < end && out.push({ id, date: d });
  const y0 = start.getFullYear();
  COMPLIANCE_RULES.filter((r) => !r.when || flags[r.when]).forEach((r) => {
    for (let y = y0 - 2; y <= y0 + 1; y++) {
      if (r.kind === "annual") push(r.id, new Date(y, r.month - 1, r.day));
      if (r.kind === "fye") push(r.id, lastDay(y, fyeMonth - 1 + r.months));
      for (let m = 0; m < 12; m++) {
        if (r.kind === "quarterly" && m % 3 === 2) push(r.id, new Date(y, m + r.monthsAfter, r.day));
        if (r.kind === "monthly") push(r.id, new Date(y, m, r.day));
        if (r.kind === "monthEnd") push(r.id, lastDay(y, m));
      }
    }
  });
  return out.sort((a, b) => a.date - b.date);
}

// ---------- Relocation Roadmap (week windows from kick-off) ----------
export const ROADMAP_STEPS = [
  { id: "assessment", phase: "plan", weeks: [0, 2] },
  { id: "company", phase: "setup", weeks: [1, 3], when: (a) => !a.hasCompany },
  { id: "substance", phase: "setup", weeks: [2, 6] },
  { id: "bank", phase: "setup", weeks: [3, 8] },
  { id: "permit_eu", phase: "move", weeks: [6, 10], when: (a) => a.citizenship === "eu" },
  { id: "permit_employment", phase: "move", weeks: [4, 12], when: (a) => a.citizenship === "non_eu" && a.route === "employment" },
  { id: "permit_nomad", phase: "move", weeks: [4, 10], when: (a) => a.citizenship === "non_eu" && a.route === "nomad" },
  { id: "permit_investment", phase: "move", weeks: [4, 18], when: (a) => a.citizenship === "non_eu" && a.route === "investment" },
  { id: "family", phase: "move", weeks: [8, 16], when: (a) => a.family === "family" },
  { id: "housing", phase: "move", weeks: [4, 9] },
  { id: "registrations", phase: "arrive", after: true, duration: 2 },
  { id: "residency_60", phase: "tax", after: true, duration: 9, when: (a) => a.rule === "d60" },
  { id: "residency_183", phase: "tax", after: true, duration: 27, when: (a) => a.rule === "d183" },
  { id: "non_dom", phase: "tax", after: true, duration: 2 },
];
export const ROADMAP_PHASES = ["plan", "setup", "move", "arrive", "tax"];

export function buildRoadmap(answers) {
  const steps = ROADMAP_STEPS.filter((s) => !s.when || s.when(answers));
  const fixed = steps.filter((s) => !s.after).map((s) => ({ ...s, start: s.weeks[0], end: s.weeks[1] }));
  const arrival = Math.max(...fixed.map((s) => s.end));
  const residency = steps.find((s) => s.id.startsWith("residency_"));
  const after = steps.filter((s) => s.after).map((s) => {
    if (s.id === "non_dom") {
      const st = arrival + (residency?.duration || 0);
      return { ...s, start: st, end: st + s.duration };
    }
    return { ...s, start: arrival, end: arrival + s.duration };
  });
  const all = [...fixed, ...after];
  return { steps: all, total: Math.max(...all.map((s) => s.end)), arrival };
}

// ---------- Formatting ----------
export const numberLocale = (lang) => `${(lang || "en").split("-")[0]}-u-nu-latn`;
export const formatEUR = (n, lang) =>
  new Intl.NumberFormat(numberLocale(lang), { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n || 0));
export const formatPct = (n, lang, digits = 1) =>
  new Intl.NumberFormat(numberLocale(lang), { style: "percent", maximumFractionDigits: digits }).format(n || 0);
